import React from "react";
import { useTranslation } from "react-i18next";
import type { AmendmentChange } from "services/contract-amendment.service";
import { EMPTY_CHANGE_ROW, shouldHintTerms } from "./employment-terms";
import type { EmploymentTermsDraft } from "./employment-terms";

interface AmendmentChangesEditorProps {
  changes: AmendmentChange[];
  onChange: React.Dispatch<React.SetStateAction<AmendmentChange[]>>;
  /** The form's position / salary / hours part — decides whether to show the hint. */
  termsDraft: EmploymentTermsDraft;
}

/** The free-text «contract point → new text» rows of an amendment form. */
export default function AmendmentChangesEditor({ changes, onChange, termsDraft }: AmendmentChangesEditorProps) {
  const { t } = useTranslation();

  const updateRow = (idx: number, field: keyof AmendmentChange, value: string) =>
    onChange((prev) => prev.map((change, i) => (i === idx ? { ...change, [field]: value } : change)));

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">{t("change_point")}</label>
      {changes.map((change, idx) => (
        <div key={idx} className="flex flex-col md:flex-row gap-2 mb-3 p-3 bg-gray-50 rounded-md">
          <select
            className="border border-gray-300 rounded-md px-2 py-1.5 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={change.action}
            onChange={(e) => updateRow(idx, "action", e.target.value)}
          >
            <option value="change">{t("change_existing")}</option>
            <option value="new">{t("new_point")}</option>
            <option value="remove">{t("remove_point")}</option>
          </select>
          <input
            type="text"
            placeholder={t("change_point")}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 md:w-32"
            value={change.point}
            onChange={(e) => updateRow(idx, "point", e.target.value)}
          />
          <textarea
            placeholder={t("change_text")}
            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px]"
            value={change.text}
            onChange={(e) => updateRow(idx, "text", e.target.value)}
          />
          {changes.length > 1 && (
            <button
              type="button"
              className="self-start px-2 py-1.5 text-red-600 hover:text-red-800 text-sm font-medium"
              onClick={() => onChange((prev) => prev.filter((_, i) => i !== idx))}
            >
              &times;
            </button>
          )}
        </div>
      ))}
      {shouldHintTerms(changes, termsDraft) && (
        <p className="mb-3 text-xs text-gray-500">{t("amendment_terms_hint")}</p>
      )}
      <button
        type="button"
        className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm max-md:min-h-11"
        onClick={() => onChange((prev) => [...prev, EMPTY_CHANGE_ROW])}
      >
        + {t("add_change")}
      </button>
    </div>
  );
}
